// Derive the printed-token "advised by" attribution from the registration's
// selected lines: DOCTOR (+ the first advising doctor's name) when a doctor
// advised the work, else SELF.
//   - Lab Visit  → the doctor-advised investigation lines carry
//     `advised_by_doctor_name` (origin doctor of the lab order).
//   - other providers (Nurse) → the procedure lines carry `suggesting_doctor_name`.
// Kept pure + standalone so it's unit-testable and reused by RegisterVisitForm.
export function advisedByFromLines(providerType, { procedureLines = [], investigationLines = [] } = {}) {
  let name = null;
  if (providerType === 'LAB') {
    const advised = (investigationLines || []).find(
      (l) => l && l.source === 'advised' && l.advised_by_doctor_name,
    );
    name = (advised && advised.advised_by_doctor_name) || null;
  } else {
    const suggested = (procedureLines || []).find((l) => l && l.suggesting_doctor_name);
    name = (suggested && suggested.suggesting_doctor_name) || null;
  }
  return { advised_by: name ? 'DOCTOR' : 'SELF', advised_by_doctor_name: name };
}

// "Advised by" display name for a visit row (BUSY-VOUCHER R1, OR-48/OR-54):
// the bare doctor name or null (→ SELF). One shared reader for the token and
// the printed queue sheet so the two never drift.
//   - IPD-lab row (`is_ipd_lab`) → `doctor_name` = the ordering doctor.
//   - everything else → the stored `advised_by_doctor_name`.
// A leading "Dr"/"Dr."/"Dr " is stripped (the caller adds exactly one "Dr. "),
// so a typed outside name like "Dr. Sharma" is never doubled. "Drake" is a
// name, not a prefix, and stays.
const DR_PREFIX = /^dr(\.|\s+|$)\s*/i;
export function advisedByName(visit) {
  const raw = visit?.is_ipd_lab ? visit.doctor_name : visit?.advised_by_doctor_name;
  const name = String(raw ?? '').trim().replace(DR_PREFIX, '').trim();
  return name || null;
}

// What the register form stores on a Lab visit at save (R1-8): the explicit
// "Advised by doctor" pick/typed name when given, else today's auto pick from
// the selected lines (first advised doctor, else SELF — OR-45).
export function resolveAdvisedBy(providerType, { explicitName, procedureLines, investigationLines } = {}) {
  const explicit = String(explicitName ?? '').trim();
  if (explicit) return { advised_by: 'DOCTOR', advised_by_doctor_name: explicit };
  return advisedByFromLines(providerType, { procedureLines, investigationLines });
}

/**
 * Provider-specific rows for the printed token, from a visit record. Used by
 * both the registration-time print and the history reprint so they agree.
 *   - OPD: Doctor row = consulting doctor; no Advised-by / assignee rows.
 *   - Nurse: Doctor row = the ADVISING doctor (blank → "Dr. —"), plus an
 *     "Advised by" row (Doctor/Self) and the assignee (nurse).
 *   - Lab (BUSY-VOUCHER R1, OR-48): ONE "Advised by" row — "Dr. {name}" or
 *     SELF — replaces the Doctor + Advised-by rows; plus the lab technician.
 *   - OTC (visit_type NURSE) attended by a DOCTOR (W3 XOR — no nurse, an
 *     attending doctor): the attending doctor IS the provider, so it fills the
 *     Doctor row directly (labelled by role, no separate assignee row), while
 *     the "Advised by" row still names who advised the procedures.
 *     Re-derived from the visit row so reprints never retro-rename.
 */
export function tokenProviderFields(visit = {}) {
  const type = visit.visit_type;
  if (type === 'NURSE' && !visit.nurse_name && visit.doctor_name) {
    return {
      doctor_name: visit.doctor_name,
      advised_by: visit.advised_by || 'SELF',
    };
  }
  if (type === 'NURSE') {
    return {
      doctor_name: visit.advised_by_doctor_name || '',
      advised_by: visit.advised_by || 'SELF',
      assignee_label: 'Nurse',
      assignee_name: visit.nurse_name || '',
    };
  }
  if (type === 'LAB') {
    const name = advisedByName(visit);
    return {
      advised_by_label: name ? `Dr. ${name}` : 'SELF',
      assignee_label: 'Lab Technician',
      assignee_name: visit.lab_technician_name || '',
    };
  }
  return { doctor_name: visit.doctor_name || '' };
}
